import { useEffect, useState } from "react";
import { fetchById } from "services/orders.service";
import {
  BillingPeriodMode,
  defaultBillingPeriodMode,
  toMonthInput,
} from "features/billing-period/billing-period";
import { termFromEndDate } from "features/billing-period/contract-term-field";
import { vilniusTodayYmd, vilniusYmd } from "utils/vilnius-date";
import { Product, ProductGroupItem } from "types/Product";
import { BillingSettings, NO_BILLING } from "./invoices-section";

/** What the order form opens with: the order being edited, or the blanks of a new one. */
export type OrderStart = {
  description: string;
  date: string;
  responsibleUser: string;
  /** Null for a new order; the customer-scoped routes fix one themselves. */
  customerId: string | null;
  lines: Product[];
  productGroups: ProductGroupItem[];
  billing: BillingSettings;
  /** The end date as loaded — sent back only if the user changes it. */
  contractEndDate: string;
  /** Unknown for an order not saved yet: created today, periods from the server. */
  createdAt: string | null;
  capPeriods: Record<BillingPeriodMode, string> | null;
  /** An external contract: signed elsewhere, carrying its own number. */
  isExternal: boolean;
  externalNumber: string;
  /** A closed order's invoice settings are frozen server-side (orders/patch.js). */
  isClosed: boolean;
};

export function newOrderStart(responsibleUser: string): OrderStart {
  return {
    description: "",
    date: vilniusTodayYmd(),
    responsibleUser,
    customerId: null,
    lines: [],
    productGroups: [],
    billing: NO_BILLING,
    contractEndDate: "",
    createdAt: null,
    capPeriods: null,
    isExternal: false,
    externalNumber: "",
    isClosed: false,
  };
}

/** An order line row as the server returns it, shaped as the products picker holds it. */
function toProductLine(dbItem: any): Product {
  return {
    id: dbItem.id,
    title: dbItem.productTitle,
    price: dbItem.unitPrice ?? "",
    quantity: dbItem.quantity ?? "",
    product: dbItem.product,
    notes: dbItem.description ?? undefined,
    sku: dbItem.sku ?? "",
  };
}

function toOrderStart(order: any): OrderStart {
  // The Vilnius day, not the browser's: read with date-fns west of UTC the
  // stored midnight became the day before and was saved back.
  const contractEndDate = vilniusYmd(order.contractEndDate);
  return {
    description: order.description || "",
    date: vilniusYmd(order.date) || vilniusTodayYmd(),
    responsibleUser: order.responsibleUser?.id || "",
    customerId: order.customer ? order.customer.id : null,
    lines: order.items.map(toProductLine),
    productGroups: (order.productGroups || []).map((group: any) => ({
      id: group.id,
      productGroup: group.productGroup?.id || group.productGroup,
      quantity: group.quantity || 1,
      price: group.unitPrice || 0,
      notes: group.description || "",
    })),
    billing: {
      isRecurring: !!order.isRecurring,
      term: termFromEndDate(contractEndDate),
      scheduleMode:
        order.invoiceScheduleMode === "specific_day"
          ? "specific_day"
          : "last_day_of_month",
      scheduleDay: order.invoiceScheduleDay
        ? String(order.invoiceScheduleDay)
        : "1",
      periodMode:
        order.billingPeriodModeEffective ||
        order.billingPeriodMode ||
        defaultBillingPeriodMode(order.invoiceScheduleMode),
      startMonth: toMonthInput(order.billingStartDate),
    },
    contractEndDate,
    createdAt: order.createdAt ?? null,
    capPeriods: order.billingCapPeriods ?? null,
    isExternal: order.origin === "external",
    externalNumber: order.externalNumber || "",
    isClosed: !!order.isClosed,
  };
}

/**
 * Fetches the order being edited. Nothing is loaded (and nothing is "loading")
 * for a new order, which has no `orderId`.
 */
export function useLoadedOrder(
  orderId: string | undefined,
  companyId: string | undefined
) {
  const [loadedOrder, setLoadedOrder] = useState<OrderStart | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    fetchById(orderId, companyId!)
      .then(({ orderEntity }) => setLoadedOrder(toOrderStart(orderEntity)))
      .catch((e: unknown) => {
        console.error(e);
        setLoadFailed(true);
      });
  }, [orderId, companyId]);

  const isLoading = !!orderId && !loadedOrder && !loadFailed;
  return { loadedOrder, isLoading, loadFailed };
}
